import { Injectable, signal } from '@angular/core';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { ApiService } from '../api-service/api-service';
import { AuthService } from '../auth/auth.service';
import { API } from '../../API';

export interface Joueur {
  person_id: number;
  player_code: string | null;
  player_last_name: string;
  player_first_name: string;
  player_last_name_ara: string | null;
  player_first_name_ara: string | null;
  player_gender: string | null;
  player_birthday: string | null;
  player_birth_city: string | null;
  player_birth_city_label?: string | null;
  player_birth_country: string | null;
  player_birth_country_label?: string | null;
  player_local_nationality: 'O' | 'N';
  player_double_nationality: 'O' | 'N';
  player_nationality_first: string | null;
  player_nationality_first_label?: string | null;
  player_nationality_second: string | null;
  player_nationality_second_label?: string | null;
  player_identity_number: string | null;
  player_passeport_number: string | null;
  player_passeport_foreign_number: string | null;
  player_residence_permit_number: string | null;
  player_license_number: string | null;
  player_email: string | null;
  player_phone: string | null;
  player_picture?: string | null;
  player_adress_description: string | null;
  player_adress_cp: string | null;
  player_adress_city: string | null;
  player_adress_city_label?: string | null;
  player_adress_country: string | null;
  player_adress_country_label?: string | null;
  player_legal_representative_relation: string | null;
  player_legal_representative_name: string | null;
  player_legal_representative_phone: string | null;
  player_legal_representative_mail: string | null;
  player_job_type: string | null;
  player_school_type: string | null;
  player_study_level: string | null;
  player_id: number;
  player_status?: 'ACTI' | 'INAC' | 'DELE' | 'NEWPL' | 'FORPL';
  player_status_label?: string | null;
  category?: string;
  relation_id: number;
  relation_person_role: string;
  relation_status: 'ACTIVE' | 'INACTIVE' | 'TRANSFERRED';
  relation_status_private?: string;
  relation_status_private_label?: string | null;
  relation_activation_date: string | null;
  relation_desactivation_date: string | null;
  club_name?: string;
  club_code?: string;
  club_short_code?: string;
  club_id?: number;
  logo_club?: string | null;
  pending_transfer_club_name?: string | null;
  relation_type_transfer?: string | null;
  relation_type_transfer_label?: string | null;
  demande_status?: string | null;
  demande_type?: string | null;
  demande_type_label?: string | null;
  licence_number?: string | null;
}

export interface Club {
  club_id: number;
  federation_id: number;
  club_code: string;
  club_name: string;
  club_short_code?: string;
  division_club?: string;
  club_description?: string;
  club_email?: string;
  club_website?: string;
  club_phone?: string;
  club_identification_1?: string;
  club_identification_2?: string;
  club_identification_3?: string;
  club_identification_4?: string;
  club_adress_description?: string;
  club_adress_cp?: string;
  club_adress_city?: string;
  club_adress_country?: string;
  club_foundation_date?: string | null;
  club_affiliation_date?: string | null;
  Cle_CLUB: string;
  Statut_CLUB: 'ACTI' | 'INAC' | 'DESA';
  licence_status?: string | null;
  licence_status_label?: string | null;
  motif?: string | null;
  logo_club?: string | null;
  _demande_id?: number | null;
  demande_status?: string | null;
  demande_type?: string | null;
  licence_number?: string | null;
  flag_updated?: string | null;
  flag_lic_printed?: string | null;
  flag_lic_generated?: string | null;
  flag_visibility?: string | null;
  flag_visibility_label?: string | null;
  flag_visibility_color_text?: string | null;
  flag_visibility_color_bg?: string | null;
  club_adress_country_code?: string | null;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface ClubAccessResponse {
  success: boolean;
  status: 'ACTI' | 'INAC' | 'DESA';
  federation: { logo_url: string | null };
  club: Club;
}

@Injectable({
  providedIn: 'root'
})
export class ClubService {
  private clubResponse$ = new BehaviorSubject<ClubAccessResponse | null>(null);
  public clubData$ = this.clubResponse$.asObservable();
  private selectedClubIdSignal = signal<number | null>(null);

  constructor(
    private apiService: ApiService,
    private authService: AuthService
  ) {}

  // Resolve club access (get club info + status + federation logo)
  resolveClub(clubIdOrCle: string | number): Observable<ClubAccessResponse> {
    const body = {
      club_id: typeof clubIdOrCle === 'number' ? clubIdOrCle : undefined,
      cle: typeof clubIdOrCle === 'string' ? clubIdOrCle : undefined,
    };

    return new Observable(observer => {
      this.apiService.post(API.CLUB.RESOLVE, body).subscribe({
        next: (response: ClubAccessResponse) => {
          this.clubResponse$.next(response);
          observer.next(response);
        },
        error: (error) => observer.error(error),
        complete: () => observer.complete()
      });
    });
  }

  getClubData(): Observable<ClubAccessResponse | null> {
    return this.clubData$;
  }

  setSelectedClubId(clubId: number | null): void {
    this.selectedClubIdSignal.set(clubId);
  }

  getSelectedClubId(): number | null {
    return this.selectedClubIdSignal();
  }

  getClubDataSnapshot(): ClubAccessResponse | null {
    return this.clubResponse$.value;
  }

  // Get club details
  getClubDetail(clubId: number, seasonId?: number): Observable<ClubAccessResponse> {
    const url = API.CLUB.GET_DETAIL.replace(':id', clubId.toString());
    return this.apiService.get(seasonId ? `${url}?season_id=${seasonId}` : url);
  }

  // Update club information
  updateClub(clubData: any): Observable<{ success: boolean; message: string }> {
    return this.apiService.put(API.CLUB.UPDATE, clubData);
  }

  updateClubLicenceStatus(clubId: number, licenceStatus: string): Observable<{ success: boolean; message: string }> {
    return this.apiService.post(API.CLUB.UPDATE_LICENCE_STATUS.replace(':id', clubId.toString()), {
      licence_status: licenceStatus,
    });
  }

  updateClubVisibility(clubId: number, flagVisibility: 'O' | 'N'): Observable<{ success: boolean; message: string; flag_visibility: string }> {
    return this.apiService.patch(API.CLUB.UPDATE_VISIBILITY.replace(':id', clubId.toString()), {
      flag_visibility: flagVisibility,
    });
  }

  // Get club data from auth service cache (from login response)
  // This avoids the API call since club data is already available from login
  getClubFromCache(): ClubAccessResponse | null {
    const club = this.authService.getClub();
    const federation = this.authService.getFederation();
    const statusFromToken = this.authService.getClubStatusFromToken();

    if (club && federation) {
      const response: ClubAccessResponse = {
        success: true,
        status: (statusFromToken || club.Statut_CLUB) as 'ACTI' | 'INAC' | 'DESA',
        federation: { logo_url: federation.logo_url || null },
        club: club
      };
      this.clubResponse$.next(response);
      return response;
    }

    return null;
  }

  // Get club data from cache as observable
  getClubFromCache$(): Observable<ClubAccessResponse | null> {
    const cachedData = this.getClubFromCache();
    return cachedData ? of(cachedData) : of(null);
  }

  // Get all clubs (for transfer destination selection)
  getAllClubs(): Observable<{ success: boolean; data: Club[] }> {
    return this.apiService.get(API.CLUB.GET_ALL);
  }

  // Get top 5 clubs by player count
  getTopClubsByPlayers(federationId: number): Observable<{ success: boolean; data: any[] }> {
    return this.apiService.post(API.CLUB.TOP_CLUBS_BY_PLAYERS, { federation_id: federationId });
  }

  // Get clubs with no active players
  getClubsWithNoActivePlayers(federationId: number): Observable<{ success: boolean; data: { total: number } }> {
    return this.apiService.post(API.CLUB.CLUBS_NO_PLAYERS, { federation_id: federationId });
  }

  federationValidateClub(clubId: number): Observable<{ success: boolean; message: string }> {
    return this.apiService.post(API.CLUB.FEDERATION_VALIDATE, { club_id: clubId });
  }

  federationRejectClub(clubId: number, motif: string): Observable<{ success: boolean; message: string }> {
    return this.apiService.post(API.CLUB.FEDERATION_REJECT, { club_id: clubId, motif });
  }

  getClubLicencePdf(clubId: number): Observable<{ success: boolean; data: any }> {
    const url = API.CLUB.LICENCE_PDF.replace(':id', clubId.toString());
    return this.apiService.get(url);
  }
}
